import {Button} from '@wandb/weave/components';
import {Callout} from '@wandb/weave/components/Callout';
import {useCombobox} from 'downshift';
import React, {forwardRef, LegacyRef, useState} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {FixedSizeList} from 'react-window';

import {InlineComboboxAddItemInput} from './InlineComboboxAddItemInput';
import {InlineComboboxSearch} from './InlineComboboxSearch';
import {ItemData, ItemRenderer} from './ItemRenderer';

type EmptyProps = {
  label?: string;
  description?: string;
  onClearSearch: () => void;
};
// The element to show when there are no combo box results
function Empty({label, description, onClearSearch}: EmptyProps) {
  return (
    <div className="flex grow flex-col items-center px-72 pt-56">
      <Callout icon="search" color="moon" size="small" />
      {label ? <p className="mt-8 text-lg">{label}</p> : null}
      {description ? (
        <p className="mt-8 text-center text-moon-500">{description}</p>
      ) : null}
      <Button className="mt-16" variant="secondary" onClick={onClearSearch}>
        Clear search
      </Button>
    </div>
  );
}

// Implementation of the "platter" upon which all the items are laid out. This
// adds 12px to the total height to account for +8px at the top for padding and
// another +4px at the bottom for padding.
const InnerElement = forwardRef(function InnerElement(
  {style, ...rest}: any,
  ref: LegacyRef<HTMLDivElement>
) {
  return (
    <div ref={ref} style={{...style, height: style.height + 12}} {...rest} />
  );
});

// The fn that filters items within the combobox. It does this by comparing the
// input value to the itemToString-ed representation of each item in the list
// of allItems, case-insensitive. If the input value is emptyish, all results
// are returned.
function filterItems<ItemType>(
  allItems: ItemType[],
  inputValue: string | undefined,
  itemToString: (item: ItemType) => string
) {
  if (inputValue == null) {
    return allItems;
  }
  return allItems.filter(item =>
    itemToString(item)
      .toLocaleLowerCase()
      .includes(inputValue.toLocaleLowerCase())
  );
}

type ComboboxProps<ItemType> = {
  disabled?: boolean;
  /**
   * Optional. If specified, this will be used to summarize why no items match
   * the user-entered search term.
   */
  emptyLabel?: string;
  /**
   * Optional. If specified, this can provide more context to the user about
   * why no items match the user-entered search term.
   */
  emptyDescription?: string;
  /**
   * The full list of items to present to the user in the combo box. Filtering
   * is handled within the combobox so this list should remain constant.
   */
  items: ItemType[];
  /**
   * The stringified version of the item. Used for filtering and as a pass-
   * through to Downshift.
   *
   * @param item
   */
  itemToString: (item: ItemType | null) => string;
  /**
   * Required. The aria-accessible label for the combobox.
   */
  label: string;
  /**
   * If the `onAddItem` callback is specified, this will be the label for the
   * "add new item" button that appears beside the search box. Optional, the
   * default is just "add item" or similar.
   */
  newItemButtonLabel?: string;
  /**
   * If the `onAddItem` callback is spceified, this will be the placeholder for
   * the input field displayed when the user goes to add a new item. Optional,
   * the default value is something like "New item..."
   */
  newItemPlaceholder?: string;
  /**
   * Invoked when the user adds an item to the combobox. The provided callback
   * should convert the label into a new item that appears in items array. This
   * action may happen asynchronously, but may provide a rocky experience for
   * the user if that async action takes a while.
   *
   * @param itemString
   */
  onAddItem?: (itemString: string) => void;
  onSelectedItemChange: (item: ItemType | null) => void;
  placeholder?: string;
  selectedItem: ItemType | null;
};

/**
 * An implementation of a combo-box that isn't a popover menu. This component
 * expects to be in a stack of elements within a flexbox, stacked vertically.
 * The search input will never grow or shrink, and the results section will
 * grow to the available space.
 *
 * The results are rendered as a virtualized list, so the list size may be
 * unbounded, but it must be known up-front before the component renders. The
 * result list will render into a scrollable pane if there are more than can
 * fit inline, otherwise they will be rendered inline.
 *
 * @see <https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/combobox_role>
 */
export function InlineCombobox<ItemType>({
  disabled,
  emptyLabel,
  emptyDescription,
  items,
  itemToString,
  label,
  newItemButtonLabel,
  newItemPlaceholder,
  onAddItem,
  onSelectedItemChange,
  placeholder,
  selectedItem: selectedItemProp,
}: ComboboxProps<ItemType>) {
  // After the user selects an item, we want to show all the items in the list,
  // so we set this flag indicating this state. As soon as they type something
  // into the input field, it switches away from this state and becomes a search
  // box.
  const [hasJustSelectedItem, setHasJustSelectedItem] = useState(
    Boolean(selectedItemProp)
  );
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);

  const displayedItems = hasJustSelectedItem ? items : filteredItems;

  const addItem = (item: string) => {
    if (onAddItem) {
      onAddItem(item);
    }
    // Just like when they select an item, if the user adds a new item, we want
    // to show all the results after they do so.
    setHasJustSelectedItem(true);
    setIsAddingItem(false);
  };

  const {
    getLabelProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    selectedItem,
    setInputValue,
  } = useCombobox<ItemType>({
    items: displayedItems,
    itemToString,
    isOpen: true,
    isItemDisabled(item: ItemType, index: number): boolean {
      return disabled === true;
    },
    onInputValueChange(e) {
      setHasJustSelectedItem(
        e.type !== useCombobox.stateChangeTypes.InputChange
      );
      setFilteredItems(filterItems(items, e.inputValue, itemToString));
    },
    onSelectedItemChange(e) {
      setHasJustSelectedItem(true);
      onSelectedItemChange(e.selectedItem ? e.selectedItem : null);
    },
    selectedItem: selectedItemProp,
  });
  const addItemButton = onAddItem ? (
    <Button
      disabled={disabled}
      icon="add-new"
      variant="secondary"
      aria-label={newItemButtonLabel}
      onClick={() => setIsAddingItem(true)}>
      {newItemButtonLabel ?? 'Add new'}
    </Button>
  ) : null;

  const itemData: ItemData<ItemType> = {
    disabled,
    displayedItems,
    getItemProps,
    highlightedIndex,
    isAddingItem,
    itemToString,
    newItemPlaceholder,
    onAddItem: addItem,
    selectedItem,
    setIsAddingItem,
  };

  return (
    <>
      <InlineComboboxSearch
        afterInput={addItemButton}
        label={label}
        labelProps={getLabelProps()}
        inputProps={getInputProps({
          type: 'search',
          placeholder,
          disabled,
        })}
      />
      <div
        className="flex grow flex-col rounded-sm outline outline-1 outline-moon-250"
        {...getMenuProps()}>
        {displayedItems.length === 0 ? (
          <>
            {isAddingItem ? (
              <InlineComboboxAddItemInput
                disabled={disabled}
                style={{margin: 8}}
                onAddItem={addItem}
                onCancelAddItem={() => setIsAddingItem(false)}
                placeholder={newItemPlaceholder}
              />
            ) : null}
            <Empty
              label={emptyLabel}
              description={emptyDescription}
              onClearSearch={() => setInputValue('')}
            />
          </>
        ) : (
          <AutoSizer>
            {({height, width}: {height: number; width: number}) => (
              <FixedSizeList<ItemData<ItemType>>
                className="pl-8"
                height={height}
                width={width}
                innerElementType={InnerElement}
                itemSize={38}
                itemCount={displayedItems.length + (isAddingItem ? 1 : 0)}
                itemData={itemData}>
                {ItemRenderer}
              </FixedSizeList>
            )}
          </AutoSizer>
        )}
      </div>
    </>
  );
}
